
import { useState, useEffect } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { Customer } from '@/lib/supabase/customers/types';

export const useCustomerDataLoader = (customerId: string) => {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<any[]>([]);
  const [totalSpent, setTotalSpent] = useState(0);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    // For new customers, we don't need to load anything
    if (customerId === 'new') {
      setLoading(false);
      // Get organization ID from localStorage for new customers
      const orgId = localStorage.getItem('currentOrganizationId');
      if (orgId) {
        setOrganizationId(orgId);
      }
      return;
    }
    
    // Load existing customer data
    const loadCustomer = async () => {
      try {
        setLoading(true);
        
        // Get the customer details
        const { data: customerData, error } = await supabase
          .from('customers')
          .select('*')
          .eq('id', customerId)
          .single();
        
        if (error) throw error;
        
        setCustomer(customerData);
        
        // Store the organization ID
        if (customerData.organization_id) {
          setOrganizationId(customerData.organization_id);
        } else {
          // Fallback to localStorage
          const orgId = localStorage.getItem('currentOrganizationId');
          if (orgId) {
            setOrganizationId(orgId);
          }
        }
        
        // Get customer orders
        const { data: ordersData, error: ordersError } = await supabase
          .from('orders')
          .select('*')
          .eq('customer_id', customerId)
          .order('created_at', { ascending: false });
        
        if (ordersError) throw ordersError;
        
        setOrders(ordersData || []);
        
        // Calculate total spent
        const total = ordersData?.reduce((sum, order) => sum + Number(order.total_amount || 0), 0) || 0;
        setTotalSpent(total);
      } catch (error) {
        console.error('Error loading customer:', error);
        toast({
          title: 'Erreur',
          description: 'Impossible de charger les données du client',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };
    
    loadCustomer();
  }, [customerId, toast]);
  
  return { 
    customer, 
    loading, 
    orders, 
    totalSpent,
    organizationId
  };
};
